import React, { FC, Fragment } from 'react';
import { Text, View } from 'react-native';

import ColorWrapper, { WithColor } from '@styles/colors';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import { ContainerStyle } from '@interfaces/base';
import TouchableElement from '@components/common/touchableElement';
import { DashboardDrilldownScope } from '@v2/pages/dashboard/common/types/drilldown';

import {
  BREADCRUMB_ITEM_HORIZONTAL_SPACING,
  BREADCRUMB_ITEM_VERTICAL_PADDING,
  BREADCRUMB_KEY_ORDER,
  BREADCRUMB_SEPARATOR,
} from './constants';
import { useManageDashboardDrilldownBreadcrumb } from './hooks/useManageDashboardDrilldownBreadcrumb';
import { SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { DASHBOARD_PRESS_HOVER_BG_COLOR } from '@v2/pages/dashboard/common/constants/colors';

export interface DashboardDrilldownBreadcrumbProps extends WithColor, ContainerStyle {
  scope: DashboardDrilldownScope;
  entityName?: string;
}

const DashboardDrilldownBreadcrumb: FC<DashboardDrilldownBreadcrumbProps> = ({ C, scope, entityName, containerStyle }) => {
  const { labelByKey, onPressByKey } = useManageDashboardDrilldownBreadcrumb({ scope, entityName });

  return (
    <View testID={TEST_IDS.dashboardDrilldown.breadcrumb} style={[{ flexDirection: 'row', alignItems: 'center' }, containerStyle]}>
      {BREADCRUMB_KEY_ORDER.map((key, index) => {
        const label = labelByKey[key];
        const onPress = onPressByKey[key];
        return (
          <Fragment key={key}>
            {index > 0 && (
              <Text style={[font['Body'], { color: C('Label/Light/Tertiary'), marginHorizontal: scale(BREADCRUMB_ITEM_HORIZONTAL_SPACING) }]}>
                {BREADCRUMB_SEPARATOR}
              </Text>
            )}
            {onPress ? (
              <TouchableElement onPress={onPress}>
                {({ boxStyle, pressed, hovered, pressHoverColor }) => (
                  <View
                    style={[
                      boxStyle,
                      {
                        paddingHorizontal: scale(BREADCRUMB_ITEM_HORIZONTAL_SPACING),
                        paddingVertical: scale(BREADCRUMB_ITEM_VERTICAL_PADDING),
                        borderRadius: scale(SCREEN_BORDER_RADIUS),
                      },
                      (pressed || hovered) && { backgroundColor: C(pressHoverColor(DASHBOARD_PRESS_HOVER_BG_COLOR)) },
                    ]}
                  >
                    <Text style={[font['Body'], { color: C('System/Light/Blue') }]}>{label}</Text>
                  </View>
                )}
              </TouchableElement>
            ) : (
              <Text
                style={[
                  font['Subhead 1 bold'],
                  { color: C('Label/Light/Primary'), marginHorizontal: scale(BREADCRUMB_ITEM_HORIZONTAL_SPACING) },
                ]}
                numberOfLines={1}
              >
                {label}
              </Text>
            )}
          </Fragment>
        );
      })}
    </View>
  );
};

export default ColorWrapper(DashboardDrilldownBreadcrumb);
